import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "utsav-cart";
const FREE_PACKING_ABOVE = 2000;
const PACKING_FEE = 60;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch (_) {
      return [];
    }
  });
  const [isCartOpen, setCartOpen] = useState(false);
  const [isCheckoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    const open = isCartOpen || isCheckoutOpen;
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [isCartOpen, isCheckoutOpen]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product.id === product.id);
      if (found) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, qty: Math.min(i.qty + qty, 50) } : i
        );
      }
      return [...prev, { product, qty }];
    });
  };

  const setQty = (productId, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.product.id !== productId)
        : prev.map((i) => (i.product.id === productId ? { ...i, qty: Math.min(qty, 50) } : i))
    );
  };

  const remove = (productId) => setItems((prev) => prev.filter((i) => i.product.id !== productId));
  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.product.price * i.qty, 0), [items]);
  const savings = useMemo(
    () => items.reduce((s, i) => s + (i.product.mrp - i.product.price) * i.qty, 0),
    [items]
  );
  const packingFee = subtotal === 0 || subtotal >= FREE_PACKING_ABOVE ? 0 : PACKING_FEE;
  const total = subtotal + packingFee;

  const value = {
    items,
    add,
    setQty,
    remove,
    clear,
    count,
    subtotal,
    savings,
    packingFee,
    total,
    freePackingAbove: FREE_PACKING_ABOVE,
    isCartOpen,
    setCartOpen,
    isCheckoutOpen,
    setCheckoutOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
